import {COMPANY_KEYS, useRequestReferralCodeMutation} from "@/api/companyApi";
import CrownIcon from "@/assets/colorable/crown.svg?react";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import StarIcon from "@/assets/colorable/star.svg?react";
import TickBoldIcon from "@/assets/colorable/tick_bold.svg?react";
import VeryLongRightArrowIcon from "@/assets/colorable/very_long_right_arrow.svg?react";
import CompanyLogoPlaceholder from "@/assets/images/company_logo_placeholder.svg";
import RefluencedLogo from "@/assets/images/refluenced_logo_with_text_black.svg";
import Button from "@/components/Common/Button";
import FullPageLoader from "@/components/Common/FullPageLoader";
import LanguageSelector from "@/components/Common/LanguageSelector";
import NotFound from "@/components/Common/NotFound";
import OverlappingAvatars from "@/components/Common/OverlappingAvatars";
import JoinApplyForm, {JoinSchema} from "@/components/Common/referral/JoinApplyForm";
import JoinPageOpenCampaigns from "@/components/Common/referral/JoinPageOpenCampaigns";
import JoinSuccessPanel from "@/components/Common/referral/JoinSuccessPanel";
import {JoinPageResponseSerializer} from "@/generated/DjangoTypes";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {useIsLoggedIn} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {createFileRoute, Link, useRouter} from "@tanstack/react-router";
import {useFeatureFlagVariantKey} from "posthog-js/react";
import {useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";
import {z} from "zod";

const JoinSearchSchema = z.object({
  email: z.string().optional(),
  from: z.literal("brands").optional(),
  instagram: z.string().optional(),
  tiktok: z.string().optional(),
});

export const Route = createFileRoute("/join/$slug")({
  component: CompanyJoin,
  validateSearch: search => JoinSearchSchema.parse(search),
});

const REFLUENCED_INFO_URL = "https://refluenced.com/en/for-influencers";

const TERMINOLOGY_FLAG_KEY = "join-page-terminology";

const TITLE_KEY_BY_VARIANT: Record<
  "ambassador" | "influencer",
  "join_creator_community_title" | "join_creator_community_title_influencer"
> = {
  ambassador: "join_creator_community_title",
  influencer: "join_creator_community_title_influencer",
};

const FAQ_KEY_PAIRS = [
  ["faq_brand_ambassador_definition_q", "faq_brand_ambassador_definition_a"],
  ["faq_become_brand_ambassador_q", "faq_become_brand_ambassador_a"],
  ["faq_brand_ambassadors_payment_q", "faq_brand_ambassadors_payment_a"],
  ["faq_creator_definition_q", "faq_creator_definition_a"],
  ["faq_apply_creator_q", "faq_apply_creator_a"],
] as const;

function CompanyJoin() {
  const {t, i18n} = useTranslation(["referral", "common"]);
  const router = useRouter();
  const {slug} = Route.useParams();
  const search = Route.useSearch();
  const cameFromBrands = search.from === "brands";
  const isLoggedIn = useIsLoggedIn();
  const terminologyVariant =
    useFeatureFlagVariantKey(TERMINOLOGY_FLAG_KEY) === "influencer" ? "influencer" : "ambassador";

  const initialFormValues: {email: string; handle: string; social: "instagram" | "tiktok"} = {
    email: search.email ?? "",
    handle: search.instagram ?? search.tiktok ?? "",
    social: search.tiktok ? "tiktok" : "instagram",
  };

  const {data, isLoading} = useGenericQuery<JoinPageResponseSerializer>({
    queryKey: COMPANY_KEYS.qCompanyJoin(slug!),
    enabled: !!slug,
  });

  const brandName = data?.company?.company_name ?? "";
  const campaigns = data?.campaigns ?? [];
  const picture =
    data?.company?.picture320 ?? data?.company?.picture640 ?? data?.company?.picture ?? CompanyLogoPlaceholder;

  const websiteRaw = data?.company?.website?.trim();
  const websiteWithProtocol = websiteRaw && (/^https?:\/\//i.test(websiteRaw) ? websiteRaw : `https://${websiteRaw}`);
  const brandWebsiteUrl =
    websiteRaw && z.url().safeParse(websiteWithProtocol).success ? websiteWithProtocol : undefined;

  const {
    mutate: requestReferralCode,
    data: referralResponse,
    isPending,
    isSuccess,
    variables: submittedForm,
  } = useRequestReferralCodeMutation(slug ?? "");

  const referralCode = referralResponse?.referral_code;
  const submittedPlatform = submittedForm?.platform;
  const hasOpenedIntercomRef = useRef(false);
  const emailFieldRef = useRef<HTMLInputElement>(null);
  const prefilled = !!search.email && !!(search.instagram ?? search.tiktok);

  useEffect(() => {
    if (isLoggedIn) return;
    const appId = import.meta.env.VITE_INTERCOM_INFLUENCER_APP_ID;
    if (!appId) return;
    // TODO: Replace react-use-intercom with https://www.npmjs.com/package/@intercom/messenger-js-sdk
    const intercom = (window as unknown as {Intercom?: (...args: unknown[]) => void}).Intercom;
    if (typeof intercom !== "function") return;
    intercom("boot", {app_id: appId});
    intercom("onShow", () => {
      hasOpenedIntercomRef.current = true;
    });
    return () => intercom("shutdown");
  }, [isLoggedIn, slug]);

  const hasTrackedOpenRef = useRef(false);
  useEffect(() => {
    if (!data?.company || hasTrackedOpenRef.current) return;
    hasTrackedOpenRef.current = true;
    capturePostHogEvent(PostHogEvents.JoinPageOpened, {
      brandSlug: slug,
      prefilled,
      terminologyVariant,
      ...(prefilled && {platform: search.tiktok ? "TIKTOK" : "INSTAGRAM"}),
    });
  }, [data, prefilled, search.tiktok, slug]);

  useEffect(() => {
    const handle = search.instagram ?? search.tiktok;

    if (!search.email || !handle) return;
    const parsed = JoinSchema.safeParse({
      email: search.email,
      handle,
      social: search.tiktok ? "tiktok" : "instagram",
    });

    if (!parsed.success) return;

    const platform = parsed.data.social === "tiktok" ? "TIKTOK" : "INSTAGRAM";
    requestReferralCode(
      {
        email: parsed.data.email,
        handle: parsed.data.handle,
        platform,
      },
      {
        onSuccess: () => {
          updateIntercomIdentity(parsed.data.email);
          capturePostHogEvent(PostHogEvents.JoinPageEmailHandleSubmitted, {
            brandSlug: slug,
            platform,
            prefilled,
            terminologyVariant,
          });
        },
      },
    );
  }, [search, requestReferralCode, slug]);

  const updateIntercomIdentity = (email: string) => {
    if (isLoggedIn) return;
    if (hasOpenedIntercomRef.current) return;
    const appId = import.meta.env.VITE_INTERCOM_INFLUENCER_APP_ID;
    if (!appId) return;
    const intercom = (window as unknown as {Intercom?: (...args: unknown[]) => void}).Intercom;
    if (typeof intercom !== "function") return;
    intercom("update", {app_id: appId, email, user_id: email});
  };

  const pageTitle = brandName ? t("seo_title_with_brand", {brand: brandName}) : t("seo_title_default");

  useTitle(pageTitle);

  useEffect(() => {
    if (!brandName || !slug) return;
    const url = `${window.location.origin}/join/${slug}`;

    const descriptionTag = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = descriptionTag?.content ?? null;
    if (descriptionTag) descriptionTag.content = t("seo_description_with_brand", {brand: brandName});

    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = url;
    document.head.appendChild(canonical);

    const faqLanguage = i18n.resolvedLanguage === "en" ? "en" : "de";
    document.documentElement.lang = faqLanguage;

    const jsonLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: brandName,
      url,
    };
    if (data?.company?.picture) jsonLd.logo = data.company.picture;
    if (data?.company?.bio) jsonLd.description = data.company.bio;
    let jsonLdScript = document.head.querySelector<HTMLScriptElement>('script[data-seo="organization"]');
    if (!jsonLdScript) {
      jsonLdScript = document.createElement("script");
      jsonLdScript.type = "application/ld+json";
      jsonLdScript.dataset.seo = "organization";
      document.head.appendChild(jsonLdScript);
    }
    jsonLdScript.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(jsonLdScript);

    const faqJsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: faqLanguage,
      mainEntity: FAQ_KEY_PAIRS.map(([questionKey, answerKey]) => ({
        "@type": "Question",
        name: t(questionKey),
        acceptedAnswer: {"@type": "Answer", text: t(answerKey), inLanguage: faqLanguage},
      })),
    };
    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.dataset.seo = "faq";
    faqScript.textContent = JSON.stringify(faqJsonLd);
    document.head.appendChild(faqScript);

    return () => {
      if (descriptionTag && previousDescription !== null) descriptionTag.content = previousDescription;
      canonical.remove();
      jsonLdScript.remove();
      faqScript.remove();
    };
  }, [brandName, slug, data, i18n.resolvedLanguage, t]);

  const renderBackButton = () =>
    cameFromBrands && (
      <button
        onClick={() => router.history.back()}
        className="text-neutral-grey-dark hover:text-neutral-black absolute top-0 left-0 flex items-center gap-1.5 text-sm font-medium transition-colors">
        <RightChevronIcon className="h-3 shrink-0 rotate-180" />
        {t("common:modalBack")}
      </button>
    );

  const renderBrandHeader = () => (
    <div className="flex flex-col items-center gap-2 sm:gap-3">
      <img
        className="border-neutral-grey-light-dark rounded-2.5xl size-24 border-2 bg-white object-contain sm:size-29"
        src={picture}
        alt={brandName ? t("brand_logo_alt", {brand: brandName}) : t("brand_logo_alt_fallback")}
      />
      {brandWebsiteUrl ? (
        <a
          href={brandWebsiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-neutral-grey-dark hover:text-neutral-black text-center text-sm font-semibold tracking-wide uppercase no-underline transition-colors">
          {brandName}
        </a>
      ) : (
        <span className="text-neutral-grey-dark text-center text-sm font-semibold tracking-wide uppercase">
          {brandName}
        </span>
      )}
    </div>
  );

  const renderStats = () => {
    if (!data?.creators_worked_with.count && !data?.rating.average) return null;
    return (
      <div className="border-neutral-grey-light-dark bg-neutral-grey-lightest divide-neutral-grey-light-dark flex min-h-20 w-full justify-center divide-x rounded-lg border sm:min-h-25 sm:w-fit">
        {!!data?.creators_worked_with.count && data.creators_worked_with.count > 0 && (
          <div className="flex flex-col items-center justify-center gap-2 px-6 py-2 sm:px-10">
            <div className="flex flex-col items-center gap-3">
              <span className="text-xl leading-3.5 font-semibold">{data.creators_worked_with.count}</span>
              <span className="text-neutral-grey-dark text-2xs text-center leading-3 font-semibold uppercase">
                {t("creators_worked_with")}
              </span>
            </div>
            <div className="flex">
              <OverlappingAvatars pictures={(data.creators_worked_with.top_3 ?? []).map(c => c.picture)} size={20} />
            </div>
          </div>
        )}
        {!!data?.rating.average && (
          <div className="flex flex-col items-center justify-center gap-2 px-6 py-2 sm:px-10">
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-1.5">
                <StarIcon className="size-3.5 shrink-0" />
                <span className="text-xl leading-3.5 font-semibold">{`${data.rating.average}/5`}</span>
              </div>
              <span className="text-neutral-grey-dark text-2xs text-center leading-3 font-semibold uppercase">
                {t("brand_rating")}
              </span>
            </div>
            {!!data?.rating.count && data.rating.count > 0 && (
              <span className="text-neutral-grey-dark text-sm">{t("reviews_count", {count: data.rating.count})}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!slug) {
    return <NotFound />;
  }

  if (isLoading || (!!search.email && !!(search.instagram ?? search.tiktok) && isPending)) {
    return <FullPageLoader />;
  }

  if (!data?.company) {
    return <NotFound />;
  }

  return (
    <main className="bg-neutral-grey-lightest text-neutral-black flex min-h-screen flex-col items-center gap-5 pb-25">
      {!isSuccess ? (
        <>
          <section className="flex w-full flex-col items-center gap-5">
            <div className="border-neutral-grey-light-dark flex w-full flex-col items-center gap-5 rounded-b-xl border bg-white px-6 py-4 shadow-sm sm:py-10">
              <div className="relative flex flex-col items-center gap-3 sm:gap-5 lg:w-182">
                {renderBackButton()}
                <div className="absolute top-0 right-0">
                  <LanguageSelector />
                </div>
                {renderBrandHeader()}
                <h1 className="m-0 max-w-150 text-center text-2xl leading-tight font-semibold sm:text-3xl">
                  {t(TITLE_KEY_BY_VARIANT[terminologyVariant], {brand: brandName})}
                </h1>
                <p className="text-neutral-grey-dark m-0 max-w-150 text-center text-sm">
                  {t("get_paid_create_content")}
                </p>
                {renderStats()}
                {campaigns.length >= 2 && (
                  <Button
                    buttonStyle="Custom"
                    className="h-12 rounded-lg bg-black px-6 text-sm text-white enabled:hover:bg-black/85"
                    onClick={() => emailFieldRef.current?.scrollIntoView({behavior: "smooth", block: "center"})}>
                    <span className="font-medium">{t("open_campaigns_apply_cta")}</span>
                  </Button>
                )}
              </div>
            </div>
          </section>
          <div className={clsxm("flex gap-2 max-lg:contents", !campaigns.length && "contents")}>
            <JoinPageOpenCampaigns brandName={brandName} campaigns={campaigns} />
            <section className="flex h-fit w-full justify-center px-4">
              <JoinApplyForm
                emailFieldRef={emailFieldRef}
                initialValues={initialFormValues}
                isPending={isPending}
                onSubmit={values =>
                  requestReferralCode(values, {
                    onSuccess: () => {
                      window.scrollTo(0, 0);
                      updateIntercomIdentity(values.email);
                      capturePostHogEvent(PostHogEvents.JoinPageEmailHandleSubmitted, {
                        brandSlug: slug,
                        platform: values.platform,
                        prefilled,
                        terminologyVariant,
                      });
                    },
                  })
                }
              />
            </section>
          </div>
        </>
      ) : (
        <section className="flex w-full flex-col items-center gap-5">
          <div className="border-neutral-grey-light-dark flex w-full flex-col items-center rounded-b-xl border bg-white px-6 py-3 shadow-sm sm:py-10">
            <div className="relative flex flex-col items-center gap-3 sm:gap-5">
              {renderBackButton()}
              <div className="absolute top-0 right-0">
                <LanguageSelector />
              </div>
              {renderBrandHeader()}
              <span className="bg-success-light-green flex min-h-6 w-fit items-center gap-1 rounded-full px-3 py-1">
                <TickBoldIcon className="text-success-forest size-2 shrink-0" />
                <span className="text-success-forest text-2xs leading-4 font-semibold tracking-wide uppercase">
                  {t("your_code_is_ready")}
                </span>
              </span>
              <h1 className="m-0 text-center text-2xl leading-tight font-semibold sm:text-3xl">
                {t("success_start_earning_title")}
              </h1>
              <div className="flex flex-col items-center gap-2 text-center">
                <span className="text-neutral-grey-dark text-sm leading-3">
                  {t("referral_code_pre_loaded", {brand: brandName})}
                </span>
                <span className="text-neutral-grey-dark text-sm leading-3">{t("just_download_and_sign_up")}</span>
              </div>
              <div className={clsxm("flex justify-center gap-5 max-lg:contents", prefilled && "w-full max-w-3xl")}>
                {prefilled && (
                  <div className="flex-1 max-lg:hidden">
                    <JoinPageOpenCampaigns brandName={brandName} campaigns={campaigns} />
                  </div>
                )}
                <div className="flex flex-col items-center gap-5 max-lg:contents">
                  {prefilled && renderStats()}
                  <JoinSuccessPanel
                    onCopy={() =>
                      capturePostHogEvent(PostHogEvents.JoinPageReferralCodeCopied, {
                        brandSlug: slug,
                        platform: submittedPlatform,
                        prefilled,
                        terminologyVariant,
                      })
                    }
                    onDownload={() =>
                      capturePostHogEvent(PostHogEvents.JoinPageDownloadAppClicked, {
                        brandSlug: slug,
                        platform: submittedPlatform,
                        prefilled,
                        terminologyVariant,
                      })
                    }
                    referralCode={referralCode ?? ""}
                  />
                </div>
              </div>
            </div>
          </div>
          {prefilled && (
            <div className="w-full lg:hidden">
              <JoinPageOpenCampaigns brandName={brandName} campaigns={campaigns} />
            </div>
          )}
        </section>
      )}
      <section className="flex flex-col items-center gap-5">
        <span className="text-neutral-grey-dark text-2xs leading-3 font-semibold uppercase">{t("the_process")}</span>
        <ol className="flex list-none flex-col items-center justify-center gap-3 p-0 px-6 sm:flex-row sm:flex-wrap sm:gap-5">
          <li className="text-neutral-grey-dark text-sm">{t("step_verify_account")}</li>
          <VeryLongRightArrowIcon className="text-neutral-grey-dark w-7 shrink-0 max-sm:hidden" aria-hidden="true" />
          <li className="text-neutral-grey-dark text-sm">{t("step_brand_reviews_profile")}</li>
          <VeryLongRightArrowIcon className="text-neutral-grey-dark w-7 shrink-0 max-sm:hidden" aria-hidden="true" />
          <li className="flex items-center gap-2">
            <CrownIcon className="text-neutral-black size-2.5 shrink-0" aria-hidden="true" />
            <span className="text-neutral-grey-dark text-sm">{t("step_become_ambassador")}</span>
          </li>
        </ol>
      </section>
      <h3 className="text-neutral-grey-dark m-0 max-w-150 px-6 text-center text-sm font-normal">
        {t("creator_audience_description")}
      </h3>
      <a
        href={REFLUENCED_INFO_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 no-underline">
        <span className="text-neutral-black text-sm font-semibold">{t("powered_by")}</span>
        <img src={RefluencedLogo} alt={t("refluenced_logo_alt")} className="h-4 w-auto" loading="lazy" />
      </a>
      <a
        href={REFLUENCED_INFO_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-neutral-grey-dark hover:text-neutral-black text-sm underline underline-offset-2 transition-colors">
        {t("what_is_refluenced")}
      </a>
      <Link
        to="/register"
        search={{join_ref: slug, utm_medium: "referral", utm_source: "join_page"}}
        data-testid="join-brand-cta"
        onClick={() => capturePostHogEvent(PostHogEvents.JoinPageBrandCtaClicked, {brandSlug: slug})}
        className="border-neutral-grey-light-dark hover:border-primary-medium-solid mt-2 flex max-w-150 flex-col items-center gap-1 rounded-xl border bg-white px-6 py-4 text-center no-underline transition-colors">
        <span className="text-neutral-black text-sm font-semibold">{t("brand_cta_headline")}</span>
        <span className="text-neutral-grey-dark text-2xs leading-3">{t("brand_cta_subtext")}</span>
        <span className="text-primary-dark-solid mt-1 text-sm font-semibold">{t("brand_cta_button")} →</span>
      </Link>
    </main>
  );
}
